import { DocumentType, index, modelOptions, mongoose, post, pre, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import { LOCALES, type Locale } from "../../i18n/constants.js";
import { RootLogger } from "../../logging/index.js";
import { PinoModules } from "../../utils/index.js";
import { ObjectIdString, StoredVerdiIdString, VendorOpaqueIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../zod-decorator/index.js";
import { verdiIdGeneration } from "../assetIdentity/verdiIdGenerationHook.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { UserAccountFieldsWithDefaults, UserAccountSchemaTypes } from "../types/userAccount.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const logger = RootLogger.child({ module: PinoModules.models.assetIdentity });

const SCHEMA_KEY = "userAccounts" as const;
export const USER_ACCOUNT_COLLECTION_NAME = "userAccounts" as const satisfies typeof SCHEMA_KEY;

/**
 * Our record of a user, one per Descope user. SCD Type-2: each edit is a new version.
 * Create through the backend's `createUserAccount`, which records the asset identity after commit.
 */
@index({ descopeUserId: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@index({ verdiId: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
// validate, not save: verdiId is required
@pre<UserAccount>("validate", async function (this: DocumentType<UserAccount>) {
    if (this.verdiId) return;
    try {
        this.verdiId = await verdiIdGeneration.function(AssetType.USER, { relatedObjectId: this.recordId });
    } catch (e) {
        logger.error({ error: e, recordId: this.recordId }, "could not generate verdiId for user account");
        throw e;
    }
})
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: USER_ACCOUNT_COLLECTION_NAME,
        _id: true,
    },
})
@post<SchemaBase<UserAccount>>(...addSchemaKeyHook(SCHEMA_KEY))
export class UserAccount extends ScdDocument<UserAccount, UserAccountFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: StoredVerdiIdString, responseOnly: true })
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(StoredVerdiIdString) })
    public verdiId!: string;

    @validate({ zodValidator: VendorOpaqueIdString, responseOnly: true })
    @prop({ type: () => String, required: true, validate: mongooseValidatorFromZod(VendorOpaqueIdString) })
    public descopeUserId!: string;

    @validate({ zodValidator: z.enum(LOCALES), hasDefault: true })
    @prop({ type: () => String, required: true, default: "en-US", enum: LOCALES })
    public locale: Locale = "en-US";

    static getters = {} as const satisfies Record<string, (self: UserAccount) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<UserAccountSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<UserAccount, UserAccountFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

UserAccount.dtoValidators = getZodSchemaFromClass(UserAccount) as unknown as DtoValidatorTypes<UserAccountSchemaTypes>;
